import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import {
  Users, Flame, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown, Loader2, Swords, Check,
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, subWeeks, subMonths } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => format(d, 'yyyy-MM-dd');
const MAX_COMPARE = 4;

const RANGE_OPTIONS = [
  { k: 'thisweek',  label: 'This Week' },
  { k: 'lastweek',  label: 'Last Week' },
  { k: 'mtd',       label: 'Month to Date' },
  { k: 'lastmonth', label: 'Last Month' },
  { k: 'ytd',       label: 'Year to Date' },
];

const rangeDates = (k) => {
  const today = new Date();
  if (k === 'thisweek' || k === 'lastweek') {
    const anchor = k === 'lastweek' ? subWeeks(today, 1) : today;
    return [startOfWeek(anchor, { weekStartsOn: 0 }), endOfWeek(anchor, { weekStartsOn: 0 })];
  }
  if (k === 'lastmonth') {
    const lm = subMonths(today, 1);
    return [startOfMonth(lm), endOfMonth(lm)];
  }
  if (k === 'ytd') return [startOfYear(today), today];
  return [startOfMonth(today), today]; // mtd
};

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const fmtHours = (mins) => {
  if (!mins) return '0';
  return (Math.round((mins / 60) * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });
};
const fmtPct = (n) => `${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
const fmtNum = (n) => (n || 0).toLocaleString();

// List-view columns (sortable)
const COLUMNS = [
  { key: 'ap',            label: 'AP',        fmt: fmtMoney, accent: true },
  { key: 'sales',         label: 'Sales',     fmt: fmtNum },
  { key: 'work_minutes',  label: 'Hours',     fmt: fmtHours },
  { key: 'dials',         label: 'Dials',     fmt: fmtNum },
  { key: 'contacts',      label: 'Contacts',  fmt: fmtNum },
  { key: 'presentations', label: 'Pres.',     fmt: fmtNum },
  { key: 'contact_pct',   label: 'Contact %', fmt: fmtPct },
  { key: 'close_pct',     label: 'Close %',   fmt: fmtPct },
];

// Head-to-head metrics (all higher-is-better)
const H2H_METRICS = [
  { key: 'ap',            label: 'Annual Premium', fmt: fmtMoney },
  { key: 'sales',         label: 'Sales',          fmt: fmtNum },
  { key: 'dials',         label: 'Dials',          fmt: fmtNum },
  { key: 'contacts',      label: 'Contacts',       fmt: fmtNum },
  { key: 'presentations', label: 'Presentations',  fmt: fmtNum },
  { key: 'work_minutes',  label: 'Hours Worked',   fmt: fmtHours },
  { key: 'contact_pct',   label: 'Contact Rate',   fmt: fmtPct },
  { key: 'close_pct',     label: 'Close Rate',     fmt: fmtPct },
];

const Avatar = ({ agent, size = 'h-7 w-7' }) => {
  if (agent?.profile_picture) {
    return <img src={agent.profile_picture} alt="" className={`${size} rounded-full object-cover flex-shrink-0`} />;
  }
  const initials = (agent?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <span className={`${size} flex items-center justify-center rounded-full bg-[#2B3674] text-[10px] font-bold text-white flex-shrink-0`}>
      {initials}
    </span>
  );
};

export default function AgentBreakdown() {
  const { getAuthHeader } = useAuth();
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [rangeKey, setRangeKey] = useState('mtd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // user_ids
  const [sortKey, setSortKey] = useState('ap');
  const [sortDir, setSortDir] = useState('desc');
  const [compareMode, setCompareMode] = useState(false);

  // Gate: only render for admin / users with a downline
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tracker/viewable-agents`, getAuthHeader());
        if (!cancelled && Array.isArray(res.data) && res.data.length > 1) setEligible(true);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBreakdown = useCallback(async () => {
    const [start, end] = rangeDates(rangeKey);
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tracker/agent-breakdown`, {
        ...getAuthHeader(),
        params: { start_date: iso(start), end_date: iso(end) },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeKey, getAuthHeader]);

  useEffect(() => { if (open) fetchBreakdown(); }, [open, fetchBreakdown]);

  const rows = useMemo(() => {
    if (!data?.agents) return [];
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...data.agents].sort((a, b) => {
      const d = (a[sortKey] - b[sortKey]) * dir;
      return d !== 0 ? d : b.ap - a.ap;
    });
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, id];
    });
  };

  const compared = useMemo(
    () => selected.map((id) => data?.agents?.find((a) => a.user_id === id)).filter(Boolean),
    [selected, data]
  );

  const rangeLabel = RANGE_OPTIONS.find((o) => o.k === rangeKey)?.label || '';

  if (!eligible) return null;

  return (
    <>
      {/* Tile that opens the modal — placed above Lead ROI */}
      <button
        type="button"
        onClick={() => { setOpen(true); setCompareMode(false); }}
        data-testid="open-agent-breakdown"
        className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-[#0B1437] via-[#16224E] to-[#2B3674] p-5 text-left transition hover:shadow-[0_14px_36px_rgba(11,20,55,0.35)]"
      >
        <div className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-[#4318FF]/30 blur-3xl transition group-hover:bg-[#4318FF]/45" />
        <div className="pointer-events-none absolute -left-8 -bottom-16 h-36 w-36 rounded-full bg-[#01B574]/20 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4318FF] to-[#868CFF] shadow-[0_8px_20px_rgba(67,24,255,0.45)]">
            <Swords className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#868CFF]">Team Analytics</div>
            <div className="text-base font-extrabold text-white">Agent Breakdown</div>
            <div className="text-xs text-[#A3AED0]">Rank your agents and compare up to {MAX_COMPARE} head-to-head.</div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-bold text-white/80 group-hover:text-white">
            Open <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setCompareMode(false); setSelected([]); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" data-testid="agent-breakdown-modal">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="flex items-center gap-2 text-[#2B3674]">
              {compareMode
                ? <><Flame className="h-5 w-5 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.95)]" /> Head-to-Head Comparison</>
                : <><Users className="h-5 w-5 text-[#4318FF]" /> Agent Breakdown</>}
              <span className="rounded-full bg-[#F4F7FE] px-2.5 py-0.5 text-[11px] font-bold text-[#2B3674]" data-testid="breakdown-range-badge">
                {rangeLabel}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 pb-6 space-y-4">
            {compareMode ? (
              /* ============ HEAD-TO-HEAD VIEW ============ */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCompareMode(false)}
                    data-testid="back-to-breakdown"
                    className="gap-1.5 text-xs font-semibold"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to list
                  </Button>
                  <div className="text-[11px] text-[#A3AED0]">
                    <Flame className="mr-1 inline h-3 w-3 text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.9)]" /> = leads that metric
                  </div>
                </div>

                <div className="overflow-x-auto rounded-2xl bg-gradient-to-b from-[#0B1437] to-[#16224E] p-1">
                  <table className="w-full min-w-[560px]" data-testid="h2h-table">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">Metric</th>
                        {compared.map((a) => (
                          <th key={a.user_id} className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Avatar agent={a} size="h-6 w-6" />
                              <span className="text-xs font-extrabold text-white">{a.name.split(' ')[0]}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {H2H_METRICS.map((m) => {
                        const values = compared.map((a) => a[m.key] || 0);
                        const best = Math.max(...values);
                        return (
                          <tr key={m.key} className="border-t border-white/5" data-testid={`h2h-row-${m.key}`}>
                            <td className="px-4 py-3 text-[13px] font-semibold text-[#A3AED0]">{m.label}</td>
                            {compared.map((a, i) => {
                              const v = values[i];
                              const wins = v === best && best > 0;
                              return (
                                <td key={a.user_id} className="px-4 py-3 text-right">
                                  <span className={`inline-flex items-center gap-1.5 font-['Manrope'] text-[15px] font-extrabold tabular-nums ${
                                    wins ? 'text-cyan-300' : 'text-white/85'
                                  }`}>
                                    {m.fmt(v)}
                                    {wins && <Flame className="h-4 w-4 text-cyan-400 drop-shadow-[0_0_7px_rgba(34,211,238,1)]" />}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* ============ LIST VIEW ============ */
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-[#A3AED0]">
                    Select agents below to compare head-to-head <span className="font-bold text-[#2B3674]">(max {MAX_COMPARE})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={rangeKey} onValueChange={setRangeKey}>
                      <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="breakdown-range">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RANGE_OPTIONS.map((o) => (
                          <SelectItem key={o.k} value={o.k}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => setCompareMode(true)}
                      disabled={selected.length < 2}
                      data-testid="compare-agents-btn"
                      className="h-9 gap-1.5 bg-gradient-to-r from-[#4318FF] to-[#868CFF] text-xs font-bold text-white shadow-[0_8px_20px_rgba(67,24,255,0.30)] hover:opacity-95 disabled:opacity-40"
                    >
                      <Swords className="h-3.5 w-3.5" />
                      Compare {selected.length > 1 ? `${selected.length} Agents` : 'Agents'}
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin text-[#2447D6]" /></div>
                ) : rows.length === 0 ? (
                  <div className="py-14 text-center text-sm text-[#A3AED0]">No agents found.</div>
                ) : (
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="w-full min-w-[860px] text-sm" data-testid="breakdown-table">
                      <thead>
                        <tr className="border-b border-[#E9EDF7] text-left text-[10px] uppercase tracking-wide text-[#A3AED0]">
                          <th className="py-2 pr-2 w-9" />
                          <th className="py-2 pr-2 w-8">#</th>
                          <th className="py-2 pr-3">Agent</th>
                          {COLUMNS.map((c) => (
                            <th key={c.key} className="py-2 px-1 text-center">
                              <button
                                type="button"
                                onClick={() => toggleSort(c.key)}
                                data-testid={`breakdown-sort-${c.key}`}
                                className={`inline-flex items-center gap-1 mx-auto rounded-md px-2 py-1 transition-colors ${
                                  sortKey === c.key ? 'bg-[#2B3674] text-white' : 'hover:bg-[#F4F7FE]'
                                }`}
                              >
                                {c.label}
                                {sortKey === c.key
                                  ? (sortDir === 'desc' ? <ArrowDown className="h-2.5 w-2.5" /> : <ArrowUp className="h-2.5 w-2.5" />)
                                  : <ArrowUpDown className="h-2.5 w-2.5 opacity-40" />}
                              </button>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((a, i) => {
                          const isSelected = selected.includes(a.user_id);
                          const selectionFull = selected.length >= MAX_COMPARE && !isSelected;
                          return (
                            <tr
                              key={a.user_id}
                              data-testid={`breakdown-row-${a.user_id}`}
                              onClick={() => !selectionFull && toggleSelect(a.user_id)}
                              className={`border-b border-[#F4F7FE] last:border-0 transition-colors ${
                                isSelected ? 'bg-[#EEF1FF]' : 'hover:bg-[#F9FAFF]'
                              } ${selectionFull ? 'opacity-50' : 'cursor-pointer'}`}
                            >
                              <td className="py-2.5 pr-2">
                                <span
                                  data-testid={`breakdown-select-${a.user_id}`}
                                  className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition ${
                                    isSelected
                                      ? 'border-[#4318FF] bg-[#4318FF]'
                                      : 'border-[#C9D2E8] bg-white'
                                  }`}
                                >
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </span>
                              </td>
                              <td className="py-2.5 pr-2 text-[11px] font-bold text-[#A3AED0]">{i + 1}</td>
                              <td className="py-2.5 pr-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Avatar agent={a} />
                                  <span className="font-semibold text-[#2B3674] truncate">{a.name}</span>
                                  {a.is_self && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#2447D6] text-white flex-shrink-0">You</span>}
                                </div>
                              </td>
                              {COLUMNS.map((c) => {
                                const v = a[c.key] || 0;
                                return (
                                  <td key={c.key} className="py-2.5 px-1 text-center">
                                    <span className={`font-['Manrope'] font-extrabold tabular-nums ${
                                      c.accent
                                        ? (v > 0 ? 'text-[#4318FF]' : 'text-[#C9D2E8]')
                                        : (v > 0 ? 'text-[#2B3674]' : 'text-[#C9D2E8]')
                                    } ${c.accent ? 'text-[15px]' : 'text-sm'}`}>
                                      {c.fmt(v)}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
